/**
 * 节点配置共享工具（应用级唯一来源）
 *
 * 设计原则：节点名称的唯一事实来源是后端 nodes 表（GET /container/nodes）。
 * 前端**绝不**在代码里写死中文节点名——一旦后台在"节点配置"页改名，写死的值就会与库脱节，
 * 这正是此前"订单页 vs 柜档案页节点名不一致"的根因（后端 nodeNameCache 陈旧）的翻版。
 *
 * 本文件在「登录后」拉取一次节点配置，通过 Context 全局共享；查不到时回退为原始节点 code
 * （诚实，绝不返回可能错误的中文名）。后台改名后由 NodeConfig 页调用 refresh() 即时刷新。
 * 注意：GET /container/nodes 需鉴权，故仅在登录态（含刷新保活）拉取，避免预登录 401；
 * 节点配置仅订单/柜档案等登录后页面使用，未登录前无需获取。
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { containerApi, type NodeItem } from '@/api/container';
import { useAppContext } from '@/contexts/AppContext';
import { TOKEN_KEY } from '@/api/request';

/** 纯函数：根据节点代码取节点序号（缺省 1） */
export function getNodeSeqByCode(nodeCode: string | undefined | null, nodes: NodeItem[]): number {
  if (!nodeCode) return 1;
  const node = (nodes || []).find((n) => n.nodeCode === nodeCode);
  return node?.nodeSeq ?? 1;
}

/** 纯函数：根据节点代码取节点显示名。优先实时 nodes 表；查不到回退原始 code（不写死中文） */
export function getNodeNameByCode(nodeCode: string | undefined | null, nodes: NodeItem[]): string {
  if (!nodeCode) return '—';
  const node = (nodes || []).find((n) => n.nodeCode === nodeCode);
  return node?.nodeName ?? nodeCode;
}

interface NodeConfigValue {
  /** 实时节点配置（来自 GET /container/nodes） */
  nodes: NodeItem[];
  /** 首次加载中 */
  loading: boolean;
  /** 按 code 取中文名（基于 nodes） */
  getNodeName: (code?: string | null) => string;
  /** 按 code 取序号（基于 nodes） */
  getNodeSeq: (code?: string | null) => number;
  /** 主动重新拉取节点配置（在"节点配置"页改名后调用，让所有页面即时更新） */
  refresh: () => void;
}

const NodeConfigContext = createContext<NodeConfigValue>({
  nodes: [],
  loading: true,
  getNodeName: (code) => code || '—',
  getNodeSeq: () => 1,
  refresh: () => {},
});

export function NodeConfigProvider({ children }: { children: ReactNode }) {
  const [nodes, setNodes] = useState<NodeItem[]>([]);
  const [loading, setLoading] = useState(false);
  const { token } = useAppContext();

  const load = useCallback(() => {
    // 未登录（无 token）不拉取节点配置：该接口需鉴权，避免预登录 401。
    // 节点配置仅订单/柜档案等登录后页面使用，登录后由下方 effect 触发拉取。
    if (!localStorage.getItem(TOKEN_KEY)) return;
    setLoading(true);
    containerApi
      .getNodes()
      .then((res) => setNodes(Array.isArray(res) ? res : []))
      .catch((err) => console.error('加载节点配置失败', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    // 仅在登录态（含刷新保活）拉取一次节点配置；未登录不发起请求。
    if (token) {
      load();
    } else {
      setNodes([]);
    }
  }, [token, load]);

  const value: NodeConfigValue = {
    nodes,
    loading,
    getNodeName: (code) => getNodeNameByCode(code, nodes),
    getNodeSeq: (code) => getNodeSeqByCode(code, nodes),
    refresh: load,
  };

  return <NodeConfigContext.Provider value={value}>{children}</NodeConfigContext.Provider>;
}

/** 获取应用级节点配置（节点名唯一来源 = 后端 nodes 表） */
export function useNodeConfig(): NodeConfigValue {
  return useContext(NodeConfigContext);
}
